import { Eye, ShieldUser, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  TABLE_ICON_CLASS,
  TableCell,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { Button } from "@/components/ui/button";
import type { StartViewAs } from "@/lib/api";
import { isAdminRole, roleLabel } from "@/lib/roles";
import { VIEW_AS_ROLES } from "@/lib/view-as";

interface ViewAsRoleTableProps {
  /** Starts viewing the dashboard as the role. */
  onView: (target: StartViewAs) => void;
}

/**
 * The roles a developer can view the dashboard as, one row each: Lid (a new
 * colleague in no team, with only what everyone gets), Beheerder (every
 * admin page, and every tool and app through the Admins team) and Eigenaar
 * (an admin who also sees the roadmap's hours and days), each with what it
 * shows under its name and a "Bekijken als" button of its own.
 */
export function ViewAsRoleTable({ onView }: ViewAsRoleTableProps) {
  const { t } = useTranslation();
  return (
    <DataTable label={t("viewAs.roles")}>
      <TableHead>
        <TableHeadCell>{t("viewAs.column.role")}</TableHeadCell>
        <TableHeadCell align="right" className="w-px">
          <span className="sr-only">{t("viewAs.column.actions")}</span>
        </TableHeadCell>
      </TableHead>
      <tbody>
        {VIEW_AS_ROLES.map((role) => {
          const Icon = isAdminRole(role) ? ShieldUser : UserRound;
          return (
            <TableRow key={role}>
              <TableCell>
                <TableItem
                  icon={<Icon aria-hidden="true" strokeWidth={1.75} className={TABLE_ICON_CLASS} />}
                  title={roleLabel(role)}
                  summary={t(`viewAs.roleAccess.${role}`)}
                />
              </TableCell>
              <TableCell align="right" className="align-middle">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-label={t("viewAs.actionFor", { name: roleLabel(role) })}
                  onClick={() => onView({ role })}
                >
                  <Eye aria-hidden="true" className="size-4" />
                  {t("viewAs.action")}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </DataTable>
  );
}
